/**
 * Admin profile hub: company Admins get the tabbed overview layout; Super Admins keep the legacy full editor.
 */

const normalizeRole = (role) =>
  String(role || '')
    .trim()
    .toUpperCase()
    .replace(/[/\s-]+/g, '_');

const hasAnyRole = (roles = [], expectedRoles = []) => {
  const normalizedExpected = expectedRoles.map(normalizeRole);
  return (roles || []).some((role) =>
    normalizedExpected.includes(normalizeRole(role)),
  );
};

/** True if any role name contains "admin" (Admin, Super Admin, Branch Admin, …). */
export function canAccessAdminPortal(roles = []) {
  if (!Array.isArray(roles) || roles.length === 0) return false;
  return roles.some((role) =>
    String(role || '')
      .toLowerCase()
      .replace(/[\s_/-]+/g, '')
      .includes('admin'),
  );
}

export const ADMIN_PORTAL_ACCESS_DENIED =
  'Access denied. Only admin users can sign in to the admin portal.';

export function readStoredUserRoles() {
  try {
    const raw = localStorage.getItem('userRoles');
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Elevated accounts: full Personal Information page, not the admin hub. */
export function isSuperAdminUser(roles) {
  return hasAnyRole(roles, ['SUPER_ADMIN', 'Super Admin']);
}

export function isBranchSuperAdminUser(roles) {
  return hasAnyRole(roles, ['BRANCH_SUPER_ADMIN', 'Branch Super Admin']);
}

/** Company Admin (and similar) without Super Admin — tabbed profile + activity overview. */
export function shouldOpenAdminProfileHub(roles) {
  if (!Array.isArray(roles) || roles.length === 0) return false;
  if (isSuperAdminUser(roles)) return false;
  // Branch Super Admin uses the same hub layout as Admin.
  return hasAnyRole(roles, [
    'ADMIN',
    'Admin',
    'BRANCH_SUPER_ADMIN',
    'Branch Super Admin',
  ]);
}

/**
 * Permission helper used by ProfileDashboard client details editor.
 * Allow super admins and admin-like roles (including branch super admin).
 */
export function canManageLeadContact(roles) {
  if (!Array.isArray(roles) || roles.length === 0) return false;
  if (isSuperAdminUser(roles)) return true;
  if (isBranchSuperAdminUser(roles)) return true;
  return hasAnyRole(roles, [
    'ADMIN',
    'Admin',
    'BRANCH_ADMIN',
    'Branch Admin',
    'SOFTWARE_ADMIN',
    'Software Admin',
  ]);
}
